import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SCHOOLS } from '../Schools/tokens';
import { formatLastGenerated } from '../Reports/lastGenerated';
import {
  fetchDeletedPartitionUploads,
  fetchPartitionUploads,
  fetchUploadFile,
  type AdminUploadListItem,
} from './uploadsAdminApi';
import { archiveFileName, collectDistrictArchive, saveZip } from './bulkDownload';
import type { ZipEntry } from './zip';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Props = {
  source?: (partition: string) => Promise<AdminUploadListItem[]>;
  deletedSource?: (partition: string) => Promise<AdminUploadListItem[]>;
  collect?: typeof collectDistrictArchive;
  save?: (entries: ZipEntry[], fileName: string) => void;
};

const bodyText = {
  fontFamily: SCHOOLS.font,
  fontSize: 16,
  lineHeight: '24px',
  color: SCHOOLS.muted,
} as const;

type Loaded = {
  key: string;
  rows: AdminUploadListItem[] | null;
  failed: boolean;
};

export function AdminUploadsDetailPage({
  source = fetchPartitionUploads,
  deletedSource = fetchDeletedPartitionUploads,
  collect = collectDistrictArchive,
  save = saveZip,
}: Props = {}) {
  const { partition = '' } = useParams();
  const [loaded, setLoaded] = useState<Loaded>({ key: partition, rows: null, failed: false });
  const [deleted, setDeleted] = useState<Loaded>({ key: partition, rows: null, failed: false });

  useEffect(() => {
    let cancelled = false;
    source(partition)
      .then((result) => {
        if (!cancelled) setLoaded({ key: partition, rows: result, failed: false });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ key: partition, rows: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [source, partition]);

  useEffect(() => {
    let cancelled = false;
    deletedSource(partition)
      .then((result) => {
        if (!cancelled) setDeleted({ key: partition, rows: result, failed: false });
      })
      .catch(() => {
        if (!cancelled) setDeleted({ key: partition, rows: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [deletedSource, partition]);

  const { rows, failed } = loaded.key === partition ? loaded : { rows: null, failed: false };
  const deletedRows = deleted.key === partition ? deleted : { rows: null, failed: false };

  return (
    <Box sx={{ display: 'flex', bgcolor: SCHOOLS.surface, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '24px 32px' }}>
        <Link
          component={RouterLink}
          to="/admin/uploads"
          sx={{ fontFamily: SCHOOLS.font, fontSize: 14 }}
        >
          Back to uploaded files
        </Link>
        <Typography
          component="h1"
          sx={{ fontFamily: SCHOOLS.font, fontSize: 24, fontWeight: 700, color: SCHOOLS.ink, mt: 1 }}
        >
          {partition}
        </Typography>
        <Typography sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}>
          Files this partition currently holds, straight from the upload store.
        </Typography>
        {failed && (
          <Typography data-testid="admin-uploads-detail-error" sx={bodyText}>
            Could not load this partition&apos;s files. Refresh to try again.
          </Typography>
        )}
        {!failed && rows === null && (
          <CircularProgress data-testid="admin-uploads-detail-loading" size={24} />
        )}
        {!failed && rows !== null && rows.length === 0 && (
          <Typography data-testid="admin-uploads-detail-empty" sx={bodyText}>
            This partition holds no files.
          </Typography>
        )}
        {!failed && rows !== null && rows.length > 0 && (
          <>
            <DownloadAll
              key={partition}
              partition={partition}
              rows={rows}
              collect={collect}
              save={save}
            />
            <FilesTable rows={rows} partition={partition} />
          </>
        )}
        <Typography
          component="h2"
          sx={{ fontFamily: SCHOOLS.font, fontSize: 18, fontWeight: 700, color: SCHOOLS.ink, mt: 4 }}
        >
          Deleted files
        </Typography>
        <Typography sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}>
          Files this district removed. They are retained and still viewable here.
        </Typography>
        {deletedRows.failed && (
          <Typography data-testid="admin-uploads-deleted-error" sx={bodyText}>
            Could not load deleted files. Refresh to try again.
          </Typography>
        )}
        {!deletedRows.failed && deletedRows.rows === null && (
          <CircularProgress data-testid="admin-uploads-deleted-loading" size={24} />
        )}
        {!deletedRows.failed && deletedRows.rows !== null && deletedRows.rows.length === 0 && (
          <Typography data-testid="admin-uploads-deleted-empty" sx={bodyText}>
            Nothing has been deleted.
          </Typography>
        )}
        {!deletedRows.failed && deletedRows.rows !== null && deletedRows.rows.length > 0 && (
          <FilesTable rows={deletedRows.rows} partition={partition} rowTestId="admin-uploads-deleted-row" />
        )}
      </Box>
    </Box>
  );
}

type DownloadState =
  | { status: 'idle' }
  | { status: 'running'; done: number; total: number }
  | { status: 'partial'; failures: string[] }
  | { status: 'failed' };

function DownloadAll({
  partition,
  rows,
  collect,
  save,
}: {
  partition: string;
  rows: AdminUploadListItem[];
  collect: typeof collectDistrictArchive;
  save: (entries: ZipEntry[], fileName: string) => void;
}) {
  const [state, setState] = useState<DownloadState>({ status: 'idle' });
  const running = state.status === 'running';

  async function run() {
    if (running) return;
    setState({ status: 'running', done: 0, total: rows.length });
    try {
      const { entries, failures } = await collect(partition, rows, fetchUploadFile, {
        onProgress: (done, total) => setState({ status: 'running', done, total }),
      });
      if (entries.length === 0) {
        setState({ status: 'failed' });
        return;
      }
      save(entries, archiveFileName(partition));
      setState(failures.length > 0 ? { status: 'partial', failures } : { status: 'idle' });
    } catch {
      setState({ status: 'failed' });
    }
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Button
        data-testid="admin-uploads-download-all"
        variant="outlined"
        size="small"
        disabled={running}
        onClick={run}
        sx={{ fontFamily: SCHOOLS.font, textTransform: 'none' }}
      >
        {running
          ? `Downloading ${state.done} of ${state.total}…`
          : `Download all ${rows.length} files (.zip)`}
      </Button>
      {state.status === 'partial' && (
        <Typography
          data-testid="admin-uploads-download-warning"
          sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mt: 1 }}
        >
          {`The archive is missing ${state.failures.length} ${
            state.failures.length === 1 ? 'file' : 'files'
          } that could not be read: ${state.failures.join(', ')}.`}
        </Typography>
      )}
      {state.status === 'failed' && (
        <Typography
          data-testid="admin-uploads-download-error"
          sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mt: 1 }}
        >
          Could not build the archive. Try again.
        </Typography>
      )}
    </Box>
  );
}

const HEADERS = ['File', 'School year', 'Version', 'Kind', 'Uploaded'];

const cellSx = {
  fontFamily: SCHOOLS.font,
  fontSize: 14,
  color: SCHOOLS.ink,
  borderBottom: `1px solid ${SCHOOLS.border}`,
} as const;

function FilesTable({
  rows,
  partition,
  rowTestId = 'admin-uploads-file-row',
}: {
  rows: AdminUploadListItem[];
  partition: string;
  rowTestId?: string;
}) {
  return (
    <Table
      size="small"
      sx={{ bgcolor: SCHOOLS.cardBg, borderRadius: '4px', boxShadow: SCHOOLS.cardShadow }}
    >
      <TableHead>
        <TableRow>
          {HEADERS.map((label) => (
            <TableCell key={label} sx={{ ...cellSx, fontWeight: 700 }}>
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id} data-testid={rowTestId}>
            <TableCell sx={cellSx}>
              <Link
                component={RouterLink}
                to={`/admin/uploads/${encodeURIComponent(partition)}/${encodeURIComponent(row.schoolYear)}/${row.version}`}
              >
                {row.name}
              </Link>
            </TableCell>
            <TableCell sx={cellSx}>{row.schoolYear}</TableCell>
            <TableCell sx={cellSx}>{row.version}</TableCell>
            <TableCell sx={cellSx}>{row.kind || '—'}</TableCell>
            <TableCell sx={cellSx}>
              {(row.uploadedAt && formatLastGenerated(row.uploadedAt)) || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
